// Production entry point. Replaces the legacy script-tag soup that loaded
// React, Babel-standalone, and every .jsx file as raw source.
//
// Load order matters:
//   1. React + ReactDOM are pinned to window so any legacy file that
//      doesn't carry its own `import React` still resolves React.* at
//      module evaluation time.
//   2. config.js wires up window.AUTH_CONFIG + window.__API_BASE__ before
//      anything that reads them (Clerk bootstrap, Supabase client, api.jsx).
//   3. The Clerk + Supabase bootstrap files register their globals.
//   4. App-layer modules (icons, api, components, screens) self-register on
//      `window.*` so the existing JSX call sites (window.I.Foo, window.Sidebar,
//      etc.) keep working untouched.
//   5. App.jsx is imported last because every other module is in window by
//      then. We render it inside HelmetProvider + BrowserRouter so the SEO
//      and routing primitives are available everywhere.

import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";

window.React = React;
window.ReactDOM = ReactDOM;

import "./config.js";
import "./lib/clerk.js";
import "./lib/supabase.js";

import "./icons.jsx";
import "./api.jsx";
import "./tweaks-panel.jsx";

import "./components/sidebar.jsx";
import "./components/topbar.jsx";
import "./components/opportunity-card.jsx";
import "./components/scan-dialog.jsx";

import "./screens/login.jsx";
import "./screens/welcome.jsx";
import "./screens/profile-setup.jsx";
import "./screens/opportunity-map.jsx";
import "./screens/dashboard-skeleton.jsx";
import "./screens/dashboard-first-scan.jsx";
import "./screens/dashboard-scanning.jsx";
import "./screens/dashboard.jsx";
import "./screens/opportunities.jsx";
import "./screens/conversations.jsx";
import "./screens/detail-panel.jsx";
import "./screens/scans.jsx";
import "./screens/notifications-settings.jsx";
import "./screens/pricing.jsx";
import "./screens/profile-view.jsx";
import "./screens/mobile.jsx";
import "./screens/onboarding-shell.jsx";
import "./screens/legal.jsx";
import "./screens/blogs.jsx";
import "./screens/alternatives.jsx";
import "./screens/why-it-works.jsx";
import "./screens/how-it-works.jsx";
import "./screens/airspace-live-app.jsx";
import "./screens/airspace-live-app-privacy.jsx";

import AppRouter from "./Router.jsx";
import "./app.jsx";

const App = window.App;
if (!App) {
  throw new Error("App failed to register on window — check src/app.jsx loaded.");
}

const container = document.getElementById("root");
const root = ReactDOM.createRoot(container);
const PlanProvider = window.PlanProvider || React.Fragment;
root.render(
  <HelmetProvider>
    <BrowserRouter>
      <PlanProvider>
        <AppRouter shell={App} />
      </PlanProvider>
    </BrowserRouter>
  </HelmetProvider>
);
